import { useEffect, useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Trophy, Flame, Target, Sparkles, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAppMode } from "@/context/AppModeContext";
import { Loader, Bullets, Chips, SectionCard } from "@/components/common";

export default function Skills() {
  const { mode } = useAppMode();
  const [p, setP] = useState(null);
  const [about, setAbout] = useState("");
  const [disc, setDisc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/progress").then((r) => setP(r.data)).catch(() => {}); }, []);

  const runDiscover = async () => {
    if (!about.trim()) { toast.info("Tell me a little about yourself first."); return; }
    setLoading(true); setDisc(null);
    try {
      const { data } = await api.post("/discover", { mode, answers: { about } });
      setDisc(data.error ? null : data);
    } catch { toast.error("Could not run discovery."); }
    finally { setLoading(false); }
  };

  const xp = p?.xp || 0;
  const pct = Math.min(100, Math.round(((xp % 200) / 200) * 100));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl">Your Progress</h1>
        <p className="text-muted-foreground mt-2">Scores show learning progress across life domains — not your worth. Keep exploring.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-border bg-card p-5 flex items-center gap-4"><div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Trophy className="w-6 h-6" /></div><div><div className="font-head font-extrabold text-3xl">Lv {p?.level || 1}</div><div className="text-sm text-muted-foreground">{xp} XP</div></div></div>
        <div className="rounded-lg border border-border bg-card p-5 flex items-center gap-4"><div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Flame className="w-6 h-6" /></div><div><div className="font-head font-extrabold text-3xl">{p?.streak || 0}</div><div className="text-sm text-muted-foreground">day streak</div></div></div>
        <div className="rounded-lg border border-border bg-card p-5 flex items-center gap-4"><div className="h-12 w-12 rounded-xl bg-primary/10 text-primary grid place-items-center"><Target className="w-6 h-6" /></div><div><div className="font-head font-extrabold text-3xl">{p?.completed_skills?.length || 0}</div><div className="text-sm text-muted-foreground">skills learned</div></div></div>
      </div>

      <div>
        <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Level {p?.level || 1} progress</span><span className="font-bold">{pct}%</span></div>
        <div className="h-3 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
      </div>

      <div className="rounded-lg border border-border bg-card p-5 sm:p-6" data-testid="radar-card">
        <h2 className="font-head font-bold text-lg mb-2">Life Skills Radar</h2>
        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={p?.radar || []} outerRadius="72%">
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="domain" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Life Skill Discovery */}
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-5 sm:p-6" data-testid="discover-card">
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Compass className="w-5 h-5 text-primary" />Help me figure out what to learn</h2>
        <p className="text-muted-foreground text-sm mt-1">Not sure where to start? Tell me about your life, situation or goals and I'll find your priorities and blind spots.</p>
        <textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={3} data-testid="discover-input"
          placeholder="e.g. I'm 19, starting college soon, living alone for the first time and worried about money…"
          className="w-full mt-3 rounded-md border border-input bg-background px-3 py-2.5 outline-none focus:ring-2 focus:ring-ring resize-none" />
        <button onClick={runDiscover} disabled={loading} data-testid="discover-run" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 transition">
          <Sparkles className="w-4 h-4" /> Discover my priorities
        </button>

        {loading && <Loader />}
        {disc && (
          <div className="mt-5 space-y-4">
            {disc.encouragement && <p className="text-base font-head font-semibold">{disc.encouragement}</p>}
            {disc.priorities?.length > 0 && (
              <SectionCard title="Your priorities">
                <ul className="space-y-2">{disc.priorities.map((pr, i) => <li key={i} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" /><span><b>{pr.area}</b> — <span className="text-muted-foreground">{pr.reason}</span></span></li>)}</ul>
              </SectionCard>
            )}
            {disc.suggested_skills?.length > 0 && <SectionCard title="Skills to learn"><Chips items={disc.suggested_skills} /></SectionCard>}
            {disc.blind_spots?.length > 0 && <SectionCard title="Possible blind spots"><Bullets items={disc.blind_spots} /></SectionCard>}
            {disc.starter_challenge && <SectionCard title="Start with this" accent><p>{disc.starter_challenge}</p></SectionCard>}
          </div>
        )}
      </div>
    </div>
  );
}
